import type { MarketTopic } from '../../types/market'

export const tradeTopics: MarketTopic[] = [
  {
    key: 'user-cart',
    slug: 'carrito',
    title: 'Organiza tus productos dentro del carrito',
    menu: 'Carrito',
    summary: 'Ajusta unidades, comprueba el saldo disponible y prepara la revisión final de tu compra.',
    kind: 'user',
    related: ['user-checkout', 'user-wallet', 'user-recharge', 'user-official'],
    aliases: ['carrito', 'agregar', 'cantidad', 'actualizar precios', 'agotado', 'stock', 'comprar'],
    questions: ['el carrito reserva el item', 'cuantos productos puedo comprar', 'el precio cambio en el carrito', 'no me alcanza el saldo'],
    checklist: ['Revisé cantidades, precios y total', 'Mi saldo disponible alcanza', 'Quité o reduje productos agotados', 'Desconecté la cuenta completa del juego', 'Elegí el personaje para los buffs, si hay buffs'],
    sections: [
      {
        id: 'no-reserva',
        title: 'Agregar no es comprar ni reservar',
        blocks: [
          { t: 'p', text: 'El carrito reúne los productos elegidos, su cantidad, precio por unidad y subtotal. No descuenta créditos y no reserva la publicación ni el stock. Otro usuario puede comprar una pieza antes de que tú confirmes.' },
          { t: 'table', head: ['Límite documentado', 'Valor'], rows: [['Productos diferentes por compra', 'Hasta 20'], ['Unidades totales por compra', 'Hasta 100'], ['Unidades por línea oficial', 'Hasta 99, según stock'], ['Unidades por publicación de jugador', '1']] },
          { t: 'p', text: 'Puedes mezclar ofertas oficiales y publicaciones de jugadores.' },
          { t: 'shot', id: '07' },
        ],
      },
      {
        id: 'cambiar',
        title: 'Quita productos o cambia unidades',
        blocks: [
          { t: 'list', items: ['Pulsa Quitar debajo del producto para retirarlo del carrito.', 'En productos oficiales, cambia Cantidad y confirma con el botón de actualización.', 'Usa Seguir explorando para buscar otros productos.', 'Si el precio cambió, usa Actualizar precios y revisa el nuevo total. Si el aviso persiste, quita la oferta y agrégala de nuevo.'] },
          { t: 'p', text: 'Un producto agotado o sin unidades suficientes debe quitarse o reducirse antes de continuar.' },
        ],
      },
      {
        id: 'saldo',
        title: 'Comprueba el resumen y tu wallet',
        blocks: [
          { t: 'p', text: 'El resumen muestra el total de la compra y el saldo disponible. Este saldo ya descuenta los créditos reservados para retiros pendientes.' },
          { t: 'p', text: 'Si no alcanza, aparece el faltante y el enlace Recargar mi wallet. Una orden de recarga pendiente no aumenta el saldo: espera a que el administrador verifique el pago.' },
        ],
      },
      {
        id: 'destino',
        title: 'Prepara el destino de la entrega',
        blocks: [
          { t: 'p', text: 'Desconecta tu cuenta completa del juego. Cuando haya buffs, selecciona el personaje que los recibirá. No necesitas seleccionar personaje para ítems enviados al Gremory de cuenta ni para VIP.' },
          { t: 'p', text: 'Si la entrega está en revisión, el personaje no está disponible o cambian las condiciones de una oferta, la pantalla bloqueará continuar y mostrará el motivo. No necesitas repetir transferencias ni recargar por ese motivo.' },
        ],
      },
      {
        id: 'revisar',
        title: 'Abre la revisión de compra',
        blocks: [
          { t: 'p', text: 'Pulsa Revisar compra. Este paso prepara la confirmación con los productos y condiciones actuales; todavía no cobra.' },
          { t: 'p', text: 'Si sales de la sesión o modificas el carrito, vuelve a revisarlo. El resultado definitivo se produce únicamente al confirmar Pagar en la siguiente pantalla.' },
        ],
      },
    ],
  },
  {
    key: 'user-checkout',
    slug: 'confirmar-compra',
    title: 'Confirma la compra y revisa su resultado',
    menu: 'Confirmar compra',
    summary: 'El pago se confirma con los productos, el destino y el importe que acabas de revisar.',
    kind: 'user',
    related: ['user-cart', 'user-delivery', 'user-wallet', 'user-faq'],
    aliases: ['pagar', 'confirmar compra', 'checkout', 'revisar compra', 'devolucion', 'cancelar compra', 'reembolso'],
    questions: ['como compro', 'se corto la conexion al pagar', 'puedo cancelar una compra', 'que pasa si falla una linea del carrito'],
    checklist: ['Comprobé nombres, cantidades y precios', 'Revisé saldo disponible, importe y saldo restante', 'Comprobé el destino de ítems y buffs', 'Mi cuenta completa está desconectada', 'Marqué la casilla y pulsé Pagar', 'Revisé Mis compras antes de repetir cualquier compra'],
    sections: [
      {
        id: 'antes-de-aceptar',
        title: 'Revisa antes de aceptar el cargo',
        blocks: [
          { t: 'steps', items: ['Comprueba nombres, cantidades y precios.', 'Revisa Saldo disponible, Importe de compra y Saldo restante.', 'Comprueba el destino de los ítems y el personaje de los buffs, si corresponde.', 'Confirma que tu cuenta completa está desconectada del juego.', 'Marca la casilla de aceptación y pulsa Pagar.'] },
          { t: 'shot', id: '08' },
          { t: 'p', text: 'La compra usa créditos ya disponibles. Esta pantalla no inicia una transferencia desde Binance ni desde una billetera externa.' },
        ],
      },
      {
        id: 'dos-condiciones',
        title: 'Dos condiciones posibles para los ítems',
        blocks: [
          { t: 'p', text: 'Con entrega al juego habilitada: la confirmación informa que los ítems irán al Gremory de cuenta, el VIP se aplicará a la cuenta y los buffs al personaje elegido.' },
          { t: 'p', text: 'Con entrega deshabilitada: las compras de ítems admitidas en ese modo quedan en resguardo del Market. La casilla exige reconocer que no se entregarán al juego todavía. Si necesitas utilizarlos de inmediato, espera a que la entrega esté habilitada.' },
        ],
      },
      {
        id: 'cambios',
        title: 'Si las condiciones cambiaron durante la revisión',
        blocks: [
          { t: 'p', text: 'La tienda comprueba otra vez saldo, disponibilidad, precios, condiciones de entrega y conexión de la cuenta al confirmar. Si alguna condición dejó de cumplirse, aparecerá un mensaje para regresar al carrito.' },
          { t: 'note', tone: 'info', text: 'Según el manual, el cobro y la compra se confirman juntos: el carrito no se completa parcialmente cuando falla una de sus líneas. Si Gremory no admite una entrega durante esta confirmación, la compra se rechaza sin realizar el cobro correspondiente a ese carrito. Es una regla documentada, no una prueba ejecutada en esta guía.' },
        ],
      },
      {
        id: 'antes-de-repetir',
        title: 'Comprueba la confirmación antes de repetir',
        blocks: [
          { t: 'p', text: 'Al finalizar, el Market muestra Compra confirmada, vacía el carrito y abre Mis compras. Allí se registra cada unidad comprada con su precio, fecha y estado de entrega.' },
          { t: 'note', tone: 'warn', text: 'Si se corta tu conexión o no ves la respuesta, revisa primero Mis compras y Mi wallet → Movimientos. No prepares una compra nueva de la misma oferta hasta comprobar si la anterior quedó confirmada.' },
        ],
      },
      {
        id: 'segun-estado',
        title: 'Continúa según el estado de entrega',
        blocks: [
          { t: 'table', head: ['Estado', 'Qué hacer'], rows: [['Enviado al Gremory', 'Entra al juego y retira desde Cuenta.'], ['Aplicado', 'El VIP o buff ya fue aplicado al destino.'], ['Resguardado', 'Consulta Mis compras → Mis ítems.']] },
          { t: 'p', text: 'No existe una devolución o cancelación automática de una compra confirmada desde el panel del comprador. Si necesitas aclarar un cobro o entrega, conserva el número de compra y solicita revisión a la administración.' },
        ],
      },
    ],
  },
  {
    key: 'user-publish',
    slug: 'publicar',
    title: 'Publica un ítem desde la mochila',
    menu: 'Vender un ítem',
    summary: 'Selecciona un ítem compatible, revisa la comisión y entrégalo al resguardo del mercado para ponerlo en venta.',
    kind: 'user',
    related: ['user-seller', 'user-wallet', 'user-delivery', 'user-faq'],
    aliases: ['vender', 'publicar', 'poner en venta', 'mochila', 'comision de venta', 'resguardar y publicar'],
    questions: ['como vendo', 'por que desaparecio mi item', 'que items puedo vender', 'cuanto queda de comision', 'cuanto recibo si vendo'],
    checklist: ['Desconecté la cuenta completa del juego', 'El objeto está en la mochila, no equipado', 'El objeto aparece habilitado', 'Revisé comisión e importe Recibirás', 'Pulsé Resguardar y publicar y vi la publicación en Mis publicaciones'],
    sections: [
      {
        id: 'preparar',
        title: 'Prepara tu cuenta y el objeto',
        blocks: [
          { t: 'p', text: 'Desconecta tu cuenta completa del juego antes de abrir la mochila del vendedor. El objeto debe estar en el inventario de un personaje, no equipado. El Market no toma objetos del equipamiento ni ofrece esta publicación desde el baúl o desde Gremory.' },
          { t: 'p', text: 'Entra en Vender → Publicar un ítem. Selecciona el personaje que contiene el objeto y pulsa Ver mochila.' },
          { t: 'shot', id: '09' },
        ],
      },
      {
        id: 'compatible',
        title: 'Selecciona una pieza compatible',
        blocks: [
          { t: 'p', text: 'La mochila muestra los ítems y sus fichas. Selecciona uno de los habilitados. Si un objeto aparece deshabilitado, lee el motivo que figura junto a él.' },
          { t: 'p', text: 'La versión documentada admite armas y armaduras convencionales, alas de primera generación y joyería compatible. No todos los grupos del juego pueden venderse: los objetos temporales o vencidos, mascotas, objetos de misión y otros tipos con datos especiales pueden quedar excluidos. La validación de la pantalla es la referencia para el objeto concreto.' },
          { t: 'p', text: 'Los iconos generales de categorías no significan que todos los objetos de esa categoría estén habilitados. VIP y buffs solo los vende el servidor.' },
        ],
      },
      {
        id: 'precio',
        title: 'Fija el precio y revisa el neto',
        blocks: [
          { t: 'p', text: 'Escribe un precio entero en la moneda web. La vista calcula la comisión porcentual y el importe Recibirás. La comisión se redondea hacia arriba al siguiente crédito entero y se descuenta únicamente si el objeto se vende.' },
          { t: 'table', example: true, caption: 'Ejemplo con una comisión del 3 % (no es la tarifa vigente)', head: ['Caso', 'Precio', 'Comisión', 'Neto para el vendedor'], rows: [['Venta de 100 créditos', '100', '3', '97'], ['Venta de 101 créditos', '101', '4', '97']] },
          { t: 'note', tone: 'example', text: 'El 3 % es solo un ejemplo. Usa siempre el porcentaje mostrado al publicar. Revisa el neto con especial atención en precios pequeños: el redondeo puede absorber todo el precio.' },
        ],
      },
      {
        id: 'resguardo',
        title: 'Confirma el resguardo y la publicación',
        blocks: [
          { t: 'p', text: 'Pulsa Resguardar y publicar. Al completarse, el ítem se retira de la mochila y queda guardado en el Market con sus datos originales. Se conserva la representación de 16 bytes del objeto, incluido su serial y opciones, para su historial y entrega.' },
          { t: 'note', tone: 'info', text: 'Que el ítem salga de tu mochila no es una pérdida: queda en custodia del Market. No se queda una copia utilizable en el personaje; la publicación representa esa pieza única. Revisa el mensaje de confirmación y el registro en Mis publicaciones.' },
        ],
      },
      {
        id: 'vigencia',
        title: 'Vigencia, límite y cambios de condiciones',
        extended: true,
        blocks: [
          { t: 'p', text: 'El número máximo de publicaciones activas y su vigencia en días los configura el administrador. Puedes consultar ambos en el espacio del vendedor. Al alcanzar el límite, deberás esperar a que una publicación se venda, venza o sea cancelada.' },
          { t: 'p', text: 'Una publicación conserva la comisión acordada al crearse aunque cambie el porcentaje para publicaciones nuevas. Si la mochila o la comisión cambian mientras preparas una venta, recarga, vuelve a seleccionar el objeto y revisa el nuevo cálculo.' },
        ],
      },
      {
        id: 'no-puedo',
        title: 'Qué hacer si no puedes publicar',
        extended: true,
        blocks: [
          { t: 'list', items: ['Cuenta conectada: sal del juego con toda la cuenta y vuelve a consultar.', 'Mochila vacía: confirma el personaje y que el objeto no esté equipado o en otro almacenamiento.', 'Objeto no habilitado: revisa el motivo; otro icono o precio no cambia su compatibilidad.', 'Máximo alcanzado: administra tus publicaciones activas.', 'Publicación pausada: espera a que el administrador reactive el servicio.'] },
          { t: 'p', text: 'Una advertencia de inventario cambiado requiere volver a cargar la mochila. Así la selección corresponde a la posición y al objeto actuales.' },
          { t: 'note', tone: 'info', text: 'La pantalla de publicación de esta versión conserva un texto heredado que dice que la entrega aún no está habilitada. Para conocer la disponibilidad real del envío de tu objeto, consulta Mis compras → Mis ítems y su botón de envío; esa pantalla utiliza el estado actual de la integración.' },
        ],
      },
    ],
  },
  {
    key: 'user-seller',
    slug: 'vendedor',
    title: 'Administra publicaciones, ventas y objetos retirados',
    menu: 'Mis publicaciones',
    summary: 'Consulta lo vendido, cancela publicaciones activas y recupera los objetos que permanecen en resguardo.',
    kind: 'user',
    related: ['user-publish', 'user-delivery', 'user-wallet', 'user-withdraw'],
    aliases: ['mis publicaciones', 'cancelar publicacion', 'publicacion vencida', 'ventas realizadas', 'cambiar precio', 'republicar'],
    questions: ['como recupero item cancelado', 'como cambio el precio', 'donde esta mi item vencido', 'cobran comision si cancelo'],
    checklist: ['Desconecté la cuenta completa', 'Cancelé la publicación activa en Vender → Mis publicaciones', 'Encontré el objeto en Mis compras → Mis ítems', 'Lo envié al Gremory de cuenta', 'Lo retiré dentro del juego desde Gremory → Cuenta'],
    sections: [
      {
        id: 'espacio',
        title: 'Consulta el espacio del vendedor',
        blocks: [
          { t: 'p', text: 'Entra en Vender. La parte superior muestra el saldo disponible, la comisión para nuevas publicaciones, su vigencia y el máximo activo permitido.' },
          { t: 'p', text: 'En Mis publicaciones aparecen el ítem, precio, comisión, neto y estado. En Ventas realizadas encontrarás las ventas confirmadas y lo recibido, con fechas en UTC.' },
          { t: 'shot', id: '10' },
        ],
      },
      {
        id: 'estados',
        title: 'Entiende cada estado de publicación',
        blocks: [
          { t: 'table', head: ['Estado', 'Qué significa'], rows: [['Activa', 'La pieza está disponible en el mercado hasta venderse, cancelarse o vencer.'], ['Vendida', 'La compra se confirmó y recibiste el importe neto en la wallet.'], ['Vencida', 'Terminó su vigencia sin venta. El objeto queda resguardado para su propietario.'], ['Cancelada', 'Retiraste la oferta. El objeto sigue resguardado en tu cuenta.']] },
          { t: 'p', text: 'Un ítem vencido deja de ser comprable. Al consultar el mercado, sus registros se actualizan al estado correspondiente.' },
        ],
      },
      {
        id: 'cancelar',
        title: 'Cancela una publicación activa',
        blocks: [
          { t: 'steps', items: ['Desconecta tu cuenta completa del juego.', 'Abre Vender → Mis publicaciones.', 'Localiza la publicación activa y pulsa Cancelar.', 'Comprueba la confirmación.', 'Abre Mis compras → Mis ítems para encontrar el objeto resguardado.'] },
          { t: 'p', text: 'Cancelar no devuelve el objeto directamente a la mochila. Tampoco cobra la comisión de venta, porque no hubo venta.' },
        ],
      },
      {
        id: 'recuperar',
        title: 'Recupera o vuelve a ofrecer la pieza',
        blocks: [
          { t: 'p', text: 'Con la entrega al juego disponible, un ítem cancelado o vencido puede enviarse desde Mis ítems al Gremory de cuenta. Después podrás retirarlo dentro del juego.' },
          { t: 'p', text: 'Esta versión no ofrece edición del precio ni republicación directa desde el resguardo. Para ofrecerlo de nuevo con otro precio, cancela la publicación activa, recupera la pieza mediante Gremory y vuelve a publicarla desde la mochila, con la cuenta desconectada.' },
        ],
      },
      {
        id: 'venta',
        title: 'Cuando se confirma una venta',
        blocks: [
          { t: 'p', text: 'El Market descuenta el precio al comprador y entrega a tu wallet el importe neto, después de la comisión acordada. Comprueba la operación en Ventas realizadas y el movimiento Venta de un ítem en la wallet.' },
          { t: 'p', text: 'Puedes usar ese saldo para comprar nuevamente o solicitar un retiro a USDT si cumples los requisitos. No necesitas reclamar manualmente el ingreso de una venta.' },
        ],
      },
    ],
  },
  {
    key: 'user-delivery',
    slug: 'compras-y-entregas',
    title: 'Consulta tus compras y recibe los ítems',
    menu: 'Recibir o recuperar ítems',
    summary: 'Sigue cada compra, diferencia el resguardo de la entrega y retira el objeto con un personaje de tu cuenta.',
    kind: 'user',
    related: ['user-checkout', 'user-seller', 'user-official', 'user-faq'],
    aliases: ['mis compras', 'mis items', 'gremory', 'resguardo', 'custodia', 'guardado', 'entrega', 'retirar item', 'retirar del gremory', 'deposito de cuenta', 'recuperar item'],
    questions: ['donde esta mi compra', 'que es gremory', 'compre pero no tengo el item', 'como retiro el item del gremory', 'como recupero mi item'],
    checklist: ['Revisé número y estado de la compra en el historial', 'Entré a la misma cuenta y servidor', 'Abrí Gremory → pestaña Cuenta', 'Si la compra es antigua con destino a personaje, usé ese personaje'],
    sections: [
      {
        id: 'historial',
        title: 'Historial de compras y Mis ítems',
        blocks: [
          { t: 'p', text: 'Mis compras → Historial de compras muestra cada compra, su número, fecha UTC, origen oficial o jugador, precio pagado y estado de entrega. Los buffs identifican el personaje de destino y el VIP corresponde a tu cuenta.' },
          { t: 'shot', id: '11' },
          { t: 'p', text: 'Mis compras → Mis ítems muestra los objetos que siguen bajo resguardo del Market, incluidos los retirados de una publicación y las compras pendientes de envío. Una pieza en venta aparece como En venta; una disponible para su propietario aparece como Resguardado.' },
        ],
      },
      {
        id: 'estados',
        title: 'Interpreta el estado de la entrega',
        blocks: [
          { t: 'table', head: ['Estado', 'Acción del usuario'], rows: [['En venta', 'La pieza sigue publicada. Cancela la publicación si quieres recuperarla.'], ['Resguardado', 'Consulta Mis ítems. Podrás enviarlo cuando la entrega esté disponible.'], ['Enviado al Gremory', 'Entra al juego y retíralo desde el depósito indicado.'], ['Aplicado', 'El VIP o buff se aplicó a la cuenta o personaje correspondiente.'], ['Entregado al juego', 'El registro indica que el objeto ya salió del resguardo hacia el juego.'], ['Entrega pendiente', 'Consulta al administrador con el número de compra si un beneficio permanece así.']] },
          { t: 'note', tone: 'warn', text: 'Enviado al Gremory confirma el envío al depósito. No significa por sí solo que ya retiraste el objeto a la mochila.' },
        ],
      },
      {
        id: 'enviar',
        title: 'Envía un objeto que está resguardado',
        blocks: [
          { t: 'steps', items: ['Desconecta tu cuenta completa del juego.', 'Abre Mis compras → Mis ítems.', 'Elige una pieza disponible; si sigue En venta, cancela antes su publicación.', 'Marca la confirmación de destino y cuenta desconectada.', 'Pulsa Enviar al Gremory de cuenta.', 'Espera la confirmación y después entra al juego.'] },
          { t: 'shot', id: '12' },
          { t: 'p', text: 'Si no aparece el botón, la entrega puede estar deshabilitada o temporalmente no disponible. El ítem permanece resguardado; no es necesario comprarlo otra vez.' },
        ],
      },
      {
        id: 'gremory',
        title: 'Retira desde el depósito compartido por cuenta',
        blocks: [
          { t: 'p', text: 'Entra con cualquiera de tus personajes de esa cuenta en el mismo servidor. Abre Gremory y selecciona la pestaña Cuenta. Allí encontrarás el objeto enviado por el Market para retirarlo a la mochila.' },
          { t: 'p', text: 'El depósito de cuenta no crea una copia para cada personaje: cualquiera de ellos puede retirar esa única pieza. Si una compra antigua indica expresamente un personaje de destino, utiliza ese personaje; el historial distingue esas entregas anteriores.' },
        ],
      },
      {
        id: 'serial',
        title: 'Opciones, serial y duración de los objetos',
        extended: true,
        blocks: [
          { t: 'p', text: 'Los ítems de jugadores conservan su serial y sus opciones originales. Los productos nuevos del servidor que llegan con serial cero reciben un serial al retirarse del Gremory; los objetos con serial existente lo conservan.' },
          { t: 'p', text: 'La duración de un ítem temporal comienza al retirarlo desde Gremory. La de VIP y buffs empieza al confirmar la compra. Son reglas diferentes: consulta el detalle antes de utilizar la oferta.' },
        ],
      },
      {
        id: 'no-aparece',
        title: 'Si no encuentras una entrega',
        blocks: [
          { t: 'list', items: ['Confirma el número y estado de la compra en el historial.', 'Comprueba que entraste a la misma cuenta y servidor.', 'Abre Gremory → Cuenta, no solo el depósito del personaje.', 'Si el historial indica una entrega antigua a un personaje, usa ese personaje.', 'Si sigue sin aparecer, informa a la administración del número de compra, fecha UTC, ítem y mensaje visible.'] },
          { t: 'note', tone: 'warn', text: 'No intentes resolver una entrega pendiente comprando de nuevo el mismo producto.' },
        ],
      },
    ],
  },
]
